import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { listData, updateData } from "@/lib/api";
import { upcomingEvents } from "@/lib/marketingData";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, CalendarStar, Sparkle, DownloadSimple, Robot } from "@phosphor-icons/react";
import ItemDetailSheet, { publishDate, fmtPublish } from "@/components/ItemDetailSheet";
import { toast } from "sonner";

const STATUS_COLOR = {
  scheduled: "bg-sky-500/15 text-sky-300 border-sky-500/20",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  published: "bg-[#6B3DFF]/15 text-[#A56CFF] border-[#6B3DFF]/20",
  failed: "bg-red-500/15 text-red-300 border-red-500/20",
};
const CHANNEL_BAR = {
  Facebook: "bg-sky-400", Instagram: "bg-pink-400", Email: "bg-emerald-400",
  SMS: "bg-violet-400", Blog: "bg-amber-400",
};
const fmtYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const localTime = (it) => {
  const d = publishDate(it);
  return d ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
};

const buildIcs = (items, name) => {
  const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/[,;]/g, (m) => "\\" + m);
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Vozelto//Marketing Calendar//EN", `X-WR-CALNAME:${esc(name)} — Vozelto`];
  items.forEach((it) => {
    const d = publishDate(it);
    if (!d) return;
    const dt = d.toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z";
    lines.push("BEGIN:VEVENT", `UID:${it.id}@vozelto.com`, `DTSTART:${dt}`,
      `SUMMARY:${esc(it.title)}`, `DESCRIPTION:${esc((it.body || "").slice(0, 500))}`, "END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
};

export default function MarketingCalendarPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ["data", "approvals", activeId];
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");
  const [drag, setDrag] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const { data: items = [] } = useQuery({ queryKey: key, queryFn: () => listData("approvals", activeId), enabled: !!activeId });
  const scheduled = useMemo(() => items.filter((i) => ["scheduled", "approved", "published", "failed"].includes(i.status) && i.scheduled_at), [items]);
  const detail = items.find((i) => i.id === detailId) || null;

  const reschedule = useMutation({
    mutationFn: ({ id, date }) => updateData("approvals", id, { scheduled_at: date, status: "scheduled" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Rescheduled"); },
  });

  const byDay = useMemo(() => {
    const m = {};
    scheduled.forEach((i) => { (m[i.scheduled_at] = m[i.scheduled_at] || []).push(i); });
    Object.values(m).forEach((l) => l.sort((a, b) => (a.meta?.post_time || "").localeCompare(b.meta?.post_time || "")));
    return m;
  }, [scheduled]);

  const days = useMemo(() => {
    if (view === "month") {
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const start = new Date(first); start.setDate(1 - first.getDay());
      return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    }
    if (view === "week") {
      const s = new Date(cursor); s.setDate(cursor.getDate() - cursor.getDay());
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
    }
    return [new Date(cursor)];
  }, [cursor, view]);

  const monthItems = useMemo(() => scheduled
    .filter((i) => i.scheduled_at?.startsWith(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`))
    .sort((a, b) => (a.scheduled_at + (a.meta?.post_time || "")).localeCompare(b.scheduled_at + (b.meta?.post_time || ""))), [scheduled, cursor]);
  const aiCount = monthItems.filter((i) => (i.created_by || "").startsWith("AI")).length;

  const shift = (dir) => { const d = new Date(cursor); if (view === "month" || view === "agenda") d.setMonth(d.getMonth() + dir); else if (view === "week") d.setDate(d.getDate() + 7 * dir); else d.setDate(d.getDate() + dir); setCursor(d); };
  const monthLabel = cursor.toLocaleString("default", { month: "long", year: "numeric" });
  const label = (view === "month" || view === "agenda") ? monthLabel : view === "week" ? `Week of ${days[0].toLocaleDateString()}` : cursor.toLocaleDateString("default", { weekday: "long", month: "long", day: "numeric" });
  const events = upcomingEvents(6);

  const exportIcs = () => {
    const ics = buildIcs(monthItems.length ? monthItems : scheduled, activeClient?.name || "Vozelto");
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vozelto-${monthLabel.toLowerCase().replace(" ", "-")}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Calendar exported — import the .ics file into Google Calendar or Outlook");
  };

  const Chip = ({ it }) => (
    <div key={it.id} draggable onDragStart={() => setDrag(it.id)} onClick={() => setDetailId(it.id)}
      data-testid={`cal-item-${it.id}`}
      title={`${it.title}\n${localTime(it)} · ${it.status}\nCreated by ${it.created_by || "Vozelto"}\n\n${(it.body || "").slice(0, 160)}`}
      className={`text-[10px] pl-1 pr-1.5 py-1 rounded border cursor-pointer flex items-center gap-1 transition-transform hover:scale-[1.03] hover:z-10 ${STATUS_COLOR[it.status] || "bg-white/10 text-white border-white/10"}`}>
      <span className={`w-1 self-stretch rounded-full shrink-0 ${CHANNEL_BAR[it.platform] || "bg-white/30"}`} />
      <span className="truncate flex-1">{it.title}</span>
      <span className="text-[8px] opacity-70 shrink-0 hidden sm:inline">{localTime(it)}</span>
    </div>
  );

  const DayCell = ({ d, big }) => {
    const inMonth = d.getMonth() === cursor.getMonth();
    const list = byDay[fmtYMD(d)] || [];
    const today = fmtYMD(d) === fmtYMD(new Date());
    return (
      <div data-testid={`cal-day-${fmtYMD(d)}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => { if (drag) reschedule.mutate({ id: drag, date: fmtYMD(d) }); setDrag(null); }}
        className={`border border-white/8 rounded-lg p-1.5 transition-colors ${big ? "min-h-[160px]" : "min-h-[92px]"} ${view === "month" && !inMonth ? "opacity-40" : ""} ${today ? "ring-1 ring-[#6B3DFF]/50" : ""} bg-[#10101A]`}>
        <div className="text-[11px] text-[#7a7a7a] mb-1">{d.getDate()}</div>
        <div className="space-y-1">
          {list.map((it) => <Chip key={it.id} it={it} />)}
        </div>
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Scheduler" title="Marketing Calendar"
        subtitle="Click any item to see exactly what it contains. Drag to reschedule. Nothing publishes without your sign-off unless Auto Pilot is on." />

      {detail && <ItemDetailSheet item={detail} onClose={() => setDetailId(null)} queryKey={key} />}

      <div className="grid lg:grid-cols-4 gap-5">
        <div className="lg:col-span-3">
          {monthItems.length > 0 && (view === "month" || view === "agenda") && (
            <div className="card-matte rounded-xl px-4 py-3 mb-3 flex items-center gap-3 fade-up" data-testid="calendar-campaign-header">
              <Robot size={22} className="brand-text shrink-0" weight="duotone" />
              <div>
                <div className="font-head font-bold">{monthLabel} Marketing Campaign</div>
                <div className="text-xs text-[#9a9a9a]">{monthItems.length} marketing assets{aiCount > 0 && <> · <span className="brand-text">{aiCount} AI-generated</span></>} · Generated by your Vozelto AI Team</div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button data-testid="cal-prev" size="sm" variant="outline" onClick={() => shift(-1)} className="border-white/15 bg-transparent text-white"><CaretLeft size={15} /></Button>
              <span className="font-head font-semibold text-lg min-w-[180px] text-center">{label}</span>
              <Button data-testid="cal-next" size="sm" variant="outline" onClick={() => shift(1)} className="border-white/15 bg-transparent text-white"><CaretRight size={15} /></Button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg bg-white/5 p-1">
                {["day", "week", "month", "agenda"].map((v) => (
                  <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${view === v ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a] hover:text-white"}`}>{v}</button>
                ))}
              </div>
              <Button data-testid="cal-export-ics" size="sm" variant="outline" onClick={exportIcs} title="Export to Google Calendar / Outlook (.ics)"
                className="border-white/15 bg-transparent text-white h-9"><DownloadSimple size={14} className="mr-1.5" /> Export</Button>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2 text-[10px] text-[#7a7a7a]" data-testid="calendar-channel-legend">
            {Object.entries(CHANNEL_BAR).map(([p, c]) => <span key={p} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${c}`} /> {p}</span>)}
          </div>

          {view === "agenda" ? (
            <div className="space-y-2" data-testid="calendar-agenda">
              {monthItems.length === 0 && <div className="text-sm text-[#7a7a7a] py-8 text-center">Nothing scheduled in {monthLabel}.</div>}
              {monthItems.map((it) => {
                const pf = fmtPublish(publishDate(it));
                return (
                  <button key={it.id} data-testid={`agenda-item-${it.id}`} onClick={() => setDetailId(it.id)}
                    className="w-full text-left card-matte rounded-xl px-4 py-3 flex items-center gap-3 hover:border-[#6B3DFF]/30 border border-transparent transition-all">
                    <span className={`w-1 self-stretch rounded-full shrink-0 ${CHANNEL_BAR[it.platform] || "bg-white/30"}`} />
                    <div className="w-32 shrink-0">
                      <div className="text-xs font-semibold text-white">📅 {pf.date.replace(`, ${cursor.getFullYear()}`, "")}</div>
                      <div className="text-[10px] text-[#9a9a9a]">🕒 {pf.time} {pf.tz}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{it.title}</div>
                      <div className="text-[10px] text-[#7a7a7a] flex items-center gap-1"><Robot size={10} /> {it.created_by || "Vozelto"}</div>
                    </div>
                    <span className={`text-[9px] font-bold rounded-full px-2 py-0.5 border shrink-0 ${STATUS_COLOR[it.status] || "bg-white/10 text-white border-white/10"}`}>{it.status}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {view === "month" && <div className="grid grid-cols-7 gap-1 mb-1">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-[10px] text-center text-[#7a7a7a] uppercase">{d}</div>)}</div>}
              <div className={`grid gap-1 ${view === "month" || view === "week" ? "grid-cols-7" : "grid-cols-1"}`} data-testid="calendar-grid">
                {days.map((d, i) => <DayCell key={i} d={d} big={view !== "month"} />)}
              </div>
            </>
          )}
          {!scheduled.length && <div className="text-xs text-[#7a7a7a] mt-3">No scheduled content yet. Generate a month in the AI Monthly Planner, or approve items in the Approval Queue.</div>}
        </div>

        {/* Seasonal planner */}
        <div className="card-matte p-5 h-fit" data-testid="seasonal-planner">
          <div className="flex items-center gap-2 mb-1"><CalendarStar size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold text-sm">Seasonal Planner</span></div>
          <p className="text-xs text-[#7a7a7a] mb-3">Upcoming events — spin up a campaign in one click.</p>
          <div className="space-y-2">
            {events.map((e) => (
              <div key={e.l} className="bg-white/[0.03] border border-white/8 rounded-lg px-3 py-2" data-testid={`event-${e.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <div><div className="text-sm text-[#cfcfcf]">{e.l}</div><div className="text-[10px] text-[#7a7a7a]">{e.date.toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" })}</div></div>
                  <button data-testid={`plan-${e.l.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => navigate(`/director?prompt=${encodeURIComponent(e.p)}`)} className="text-[#6B3DFF] hover:text-[#A56CFF]"><Sparkle size={16} weight="fill" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
